import React, { useEffect, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import { getManifests, updateManifest } from "../services/manifestService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Search, MapPin, CheckCircle, Clock, ChevronDown, ChevronRight } from "lucide-react";
import CustomDropdown from "../components/CustomDropdown";
import { destinationOptions } from "../utils/helper";

const ManifestIn = () => {
  const { user } = useSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(false);
  const [manifestList, setManifestList] = useState([]);
  
  const [searchManifest, setSearchManifest] = useState("");
  const [searchDestination, setSearchDestination] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const toggleRow = (id) => {
    setExpandedRowIds(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleGetManifestList = async () => {
    try {
      setIsLoading(true);
      // Fetch specifically without manual branch dropdown
      const branchQuery = user?.role === "EMPLOYEE" ? (user?.branch?.code || "") : "";
      const response = await getManifests(branchQuery);
      if (response?.status === 200) {
        const initializedManifests = (response?.data || []).map(m => ({
          ...m,
          status: m.status || 'PENDING'
        }));
        setManifestList(initializedManifests);
      }
    } catch (error) {
      console.error("Error fetching manifest list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetManifestList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (manifest, newStatus) => {
    // Optimistic UI update
    setManifestList((prev) =>
      prev.map((m) => (m._id === manifest._id ? { ...m, status: newStatus } : m))
    );
    
    try {
      const updatedData = { ...manifest, status: newStatus };
      const res = await updateManifest(manifest._id, updatedData);
      if (res.status === 200) {
        toast.success(`Manifest status updated to ${newStatus}`);
      } else {
        // Rollback on failure
        setManifestList((prev) =>
          prev.map((m) => (m._id === manifest._id ? { ...m, status: manifest.status } : m))
        );
        toast.error("Failed to update status");
      }
    } catch (error) {
      // Rollback on failure
      setManifestList((prev) =>
        prev.map((m) => (m._id === manifest._id ? { ...m, status: manifest.status } : m))
      );
      toast.error(error?.response?.data?.error || "Error updating manifest");
      console.error("Error updating manifest:", error);
    }
  };

  const goToPrevDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };
  
  const goToNextDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  // Local filtering based on all fields
  const filteredManifests = manifestList.filter((manifest) => {
    const manifestNo = manifest?.manifestNo || "";
    const dest = manifest?.destination || "";
    const manifestDate = manifest?.dispatchDate || manifest?.date || manifest?.bookingDate || "";
    
    const matchesManifest = manifestNo.toLowerCase().includes(searchManifest.toLowerCase());
    
    // Explicit exact matching against CustomDropdown, allowing empty selection to bypass filter
    const matchesDestination = searchDestination ? dest.toLowerCase() === searchDestination.toLowerCase() : true;
    
    // Check if any of the manifest's date properties match the selected date flexibly
    const matchesDate = (() => {
      if (!selectedDate) return true;
      
      const datesToCheck = [
          manifest?.dispatchDate,
          manifest?.date,
          manifest?.bookingDate,
          manifest?.createdAt
      ].filter(Boolean); // Drop nulls or empty strings
      
      if (datesToCheck.length === 0) return false;
      
      const [y, m, d] = selectedDate.split('-');
      
      const formatsToTry = [
          `${y}-${m}-${d}`,
          `${y}/${m}/${d}`,
          `${d}-${m}-${y}`,
          `${d}/${m}/${y}`,
          `${y}-${parseInt(m, 10)}-${parseInt(d, 10)}`,
          `${y}/${parseInt(m, 10)}/${parseInt(d, 10)}`,
          `${parseInt(d, 10)}-${parseInt(m, 10)}-${y}`,
          `${parseInt(d, 10)}/${parseInt(m, 10)}/${y}`
      ];
      
      for (const dVal of datesToCheck) {
          if (typeof dVal !== 'string') continue;
          
          if (formatsToTry.some(format => dVal.includes(format))) {
              return true;
          }
          
          // ISO literal fallback check
          if (dVal.length >= 10 && dVal.substring(0, 10) === selectedDate) {
              return true;
          }
      }

      return false;
    })();
    
    return matchesManifest && matchesDestination && matchesDate;
  });

  return (
    <div className="p-3 bg-slate-200 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
        <SectionHeader
          title="Manifest In"
          subTitle="Update arrival status for incoming manifests"
        />
        <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
          
          {/* Date Picker */}
          <div className="flex items-center gap-1 bg-white border border-gray-300 rounded px-2 py-1.5 h-[38px] text-sm">
            <button onClick={goToPrevDate} className="px-2 font-bold text-gray-500 hover:text-black">‹</button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="flex-1 px-1 bg-transparent outline-none cursor-pointer"
            />
            <button onClick={goToNextDate} className="px-2 font-bold text-gray-500 hover:text-black">›</button>
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 border border-gray-300 rounded h-[38px]">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Manifest No..."
              value={searchManifest}
              onChange={(e) => setSearchManifest(e.target.value)}
              className="px-2 text-sm bg-transparent outline-none w-32"
            />
          </div>

          {/* Destination Dropdown mapped to Helper Options */}
          <div className="w-56 z-50">
            <CustomDropdown
              placeholder="Select destination..."
              value={searchDestination}
              options={destinationOptions}
              onChange={(val) => setSearchDestination(val)}
            />
          </div>
          
          {/* Clear Destination Button (since CustomDropdown doesn't natively expose clear string internally easily) */}
          {searchDestination && (
            <button 
              onClick={() => setSearchDestination("")}
              className="px-3 py-2 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 h-[38px]"
            >
              Clear
            </button>
          )}

        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white border rounded-xl overflow-hidden mt-4">
        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading manifests...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-center">Manifest No</th>
                  <th className="px-4 py-3 border-l text-center">Dispatch Date</th>
                  <th className="px-4 py-3 border-l text-center">Destination</th>
                  <th className="px-4 py-3 border-l text-center">Vehicle No</th>
                  <th className="px-4 py-3 border-l text-center">Status</th>
                  <th className="px-4 py-3 border-l text-center">Update Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredManifests?.map((manifest) => (
                  <React.Fragment key={manifest._id}>
                    <tr className="border-t text-sm hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 border-l font-medium text-center">
                          <div className="flex items-center justify-center gap-2">
                              <button onClick={() => toggleRow(manifest._id)} className="p-1 hover:bg-gray-200 rounded text-gray-600 transition-colors">
                                  {expandedRowIds.includes(manifest._id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                              {manifest?.manifestNo}
                          </div>
                      </td>
                    <td className="px-4 py-3 border-l text-center">
                      {manifest?.dispatchDate}
                    </td>
                    <td className="px-4 py-3 border-l text-center">
                      {manifest?.destination}
                    </td>
                    <td className="px-4 py-3 border-l text-center">
                      {manifest?.vehicleNo}
                    </td>
                    <td className="px-4 py-3 border-l text-center">
                      {manifest?.status === "ARRIVED" ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                          <CheckCircle className="w-3.5 h-3.5" />
                          {manifest?.status}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                          <Clock className="w-3.5 h-3.5" />
                          {manifest?.status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 border-l text-center">
                      <select
                        value={manifest?.status}
                        onChange={(e) => handleStatusChange(manifest, e.target.value)}
                        className="bg-white border border-gray-300 text-gray-700 text-xs rounded focus:ring-sky-500 focus:border-sky-500 p-1 w-24 cursor-pointer outline-none"
                      >
                        <option value="PENDING">PENDING</option>
                        <option value="ARRIVED">ARRIVED</option>
                      </select>
                    </td>
                  </tr>
                  {expandedRowIds.includes(manifest._id) && (
                      <tr className="bg-slate-50 border-b shadow-inner">
                          <td colSpan="6" className="px-8 py-4">
                              <div className="border border-gray-200 bg-white rounded-lg shadow-sm overflow-hidden">
                                  <table className="w-full text-xs box-border">
                                      <thead className="bg-slate-100 text-gray-500 uppercase">
                                          <tr>
                                              <th className="px-4 py-2 font-semibold text-center">LR No</th>
                                              <th className="px-4 py-2 border-l font-semibold text-center">Boxes</th>
                                              <th className="px-4 py-2 border-l font-semibold text-center">Weight</th>
                                              <th className="px-4 py-2 border-l font-semibold text-center">Destination</th>
                                              <th className="px-4 py-2 border-l font-semibold text-center">Next Hub</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {manifest?.shipments?.map((ship, sIdx) => (
                                              <tr key={sIdx} className="border-t border-gray-100 hover:bg-sky-50 transition-colors">
                                                  <td className="px-4 py-2 text-center font-medium text-gray-700">{ship.lrNo}</td>
                                                  <td className="px-4 py-2 border-l text-center text-gray-700">{ship.boxes}</td>
                                                  <td className="px-4 py-2 border-l text-center text-gray-700">{ship.weight}</td>
                                                  <td className="px-4 py-2 border-l text-center text-gray-700">{ship.destination}</td>
                                                  <td className="px-4 py-2 border-l text-center text-gray-700">{ship.nextHub}</td>
                                              </tr>
                                          ))}
                                          {(!manifest?.shipments || manifest.shipments.length === 0) && (
                                              <tr>
                                                  <td colSpan="5" className="p-4 text-center text-gray-500 italic">No shipments bound to this manifest.</td>
                                              </tr>
                                          )}
                                      </tbody>
                                  </table>
                              </div>
                          </td>
                      </tr>
                  )}
                  </React.Fragment>
                ))}
                {filteredManifests?.length === 0 && (
                  <tr className="h-64">
                    <td colSpan="6" className="py-6 text-center text-gray-500">
                      No manifests found for {selectedDate} {searchDestination && `to ${searchDestination}`}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManifestIn;
